<template>
    <Card title="Dispensed">
        <b-row id="top-row">
            <b-col cols="2">
                <fg-input label="Start Date">
                    <el-date-picker :disabled="isDisabled" v-model="fromDate" type="date"></el-date-picker>
                </fg-input>
            </b-col>
            <b-col cols="2">
                <fg-input label="End Date">
                    <el-date-picker :disabled="isDisabled" v-model="toDate" type="date"></el-date-picker>
                </fg-input>
            </b-col>
            <b-col cols="2" class="pt-2">
                <b-button variant="info" :disabled="isDisabled"
                          block
                          @click="allClicked">
                    All Time
                </b-button>
            </b-col>
            <!-- <b-col cols="2" class="pt-2">
                <b-button variant="danger" :disabled="isDisabled"
                          block
                          @click="clearClicked">
                    Clear
                </b-button>
            </b-col> -->
            <b-col cols="6" class="pt-2">
                <ExportDropdown :disabled="isDisabled" @export-pdf="savePDF" @export-csv="saveCSV" />
                <span class="text-center my-2" v-if="loadingPDF">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Generating PDF...</strong>
                </span>
                <span class="text-center my-2" v-if="loadingCSV">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Generating CSV...</strong>
                </span>
            </b-col>
        </b-row>
        <b-row id="filter-row">
            <b-col>
                <b-row id="dea-filter-row">
                    <b-col>
                        <label class="pr-3">DEA Class: </label>
                        <checkbox v-model="deaSelection[0]" :click="clear=false" inline>I</checkbox>
                        <checkbox v-model="deaSelection[1]" :click="clear=false" inline>II</checkbox>
                        <checkbox v-model="deaSelection[2]" :click="clear=false" inline>III</checkbox>
                        <checkbox v-model="deaSelection[3]" :click="clear=false" inline>IV</checkbox>
                        <checkbox v-model="deaSelection[4]" :click="clear=false" inline>V</checkbox>
                        <checkbox v-model="deaSelection[5]" :click="clear=false" inline>N/A</checkbox>
                    </b-col>
                </b-row>
                <b-row id="program-filter-row">
                    <b-col>
                        <SearchComponent v-model="program"
                                         label="Program"
                                         search-u-r-l="/Program"
                                         :disabled="isLoading"
                                         clearable
                                         prepopulate
                                         @input="filterFromFirstPage"></SearchComponent>
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="store"
                            :label="`Store`"
                            :search-u-r-l="`/Store`"
                            :params="{active: true}"
                            :disabled="isLoading"
                            :prepopulate="true"
                            :clearable="true"
                            @input="filterFromFirstPage"></SearchComponent>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>

        <!-- TABLE REPORT-->
        <b-row id="report-row">
            <b-col>
                <b-row class="py-3">
                    <b-col>
                        <label for="report-table">Dispensing</label>
                        <b-table id="report-table"
                                 striped
                                 outlined
                                 foot-clone
                                 selectable
                                 responsive="true"
                                 :items="reportItems"
                                 :fields="reportFields"
                                 :sort-by="sortBy"
                                 :sort-desc="sortDesc"
                                 no-local-sorting
                                 :busy="isLoading"
                                 show-empty
                                 empty-text="No results for indicated period of time."
                                 @sort-changed="handleSort"
                        >
                            <template #table-busy>
                                <div class="text-center my-2">
                                    <b-spinner class="align-middle"></b-spinner>
                                    <strong>Loading...</strong>
                                </div>
                            </template>
                        </b-table>
                    </b-col>
                </b-row>
                <BottomPagerBar
                    :value="pageNumber"
                    :total-rows="totalCount"
                    :per-page="pageSize"
                    :loading="isLoading"
                    show-total
                    show-page-size-selector
                    item-name="dispensed item"
                    scroll-target="#report-table"
                    @input="onPageChange"
                    @change-page-size="onPageSizeChange($event)"
                ></BottomPagerBar>
            </b-col>
        </b-row>

        <div v-show="false">
            <b-table id="pdf-table"
                        striped
                        :items="pdfData"
                        :fields="pdfFields"
                        :sort-by="sortBy"
            ></b-table>
        </div>
    </Card>
</template>

<script lang="ts">
    import Checkbox from "@/components/Inputs/Checkbox.vue"
    import { Component, Watch, Mixins, Prop } from 'vue-property-decorator';
    import { DatePicker } from 'element-ui';
    import moment from 'moment';
    import { Prescription } from '@/models/Prescription';
    import { ReportExporter } from '../../mixins/ReportExporter';
    import { NotificationOptions } from "@/util/NotificationOptionsPresets";
    import SearchComponent from '@/components/SearchComponent.vue';
    import BottomPagerBar from '@/components/BottomPagerBar.vue';
    import ExportDropdown from '@/components/ExportDropdown.vue';
    import { Program } from "@/models/Program";
    import { Store } from "@/models/Store";

    @Component({
        name: "DispensedPage",
        components: {
            [DatePicker.name]: DatePicker,
            Checkbox, SearchComponent, BottomPagerBar, ExportDropdown
        },
    })
    export default class DispensedPage extends Mixins(ReportExporter) {
        created() {
            this.getReport();
        }

        //filters
        private fromDate: Date = moment().subtract(14, 'd').toDate();
        private toDate: Date = moment().toDate();
        private program: Program = new Program();
        private store: Store = new Store();
        private deaSelection: boolean[] = [true, true, true, true, true, true];

        //table settings
        private isLoading:boolean = false;
        // Sorting is server-side (no-local-sorting on the b-table): these mirror the active
        // sort and are sent to the API. Defaults match the SP's Shipped-descending order.
        private sortBy: string = "created";
        private sortDesc: boolean = true;
        private reportItems: any[] = [];
        private nameFormatter = (value: any, key: any, item: any) => {
            // Exports collapse a missing parent to an empty string, and reading .lastName off it
            // yields undefined rather than throwing, so the cell would read "undefined, undefined".
            if (!value) {
                return "";
            }
            return `${value.lastName}, ${value.firstName}`;
        };
        private ndcFormatter = (value: string) => {
            // Exports collapse a missing value to an empty string before the formatter runs, and
            // substr on it would render "--" for a line that simply carries no NDC.
            if (!value) {
                return "";
            }
            return `${value.substr(0, 5)}-${value.substr(5, 4)}-${value.substr(9)}`;
        };
        private prescriptionIdFormatter = (value: Prescription) => {
            // As above: a missing prescription would otherwise export "undefined-undefined-undefined".
            if (!value) {
                return "";
            }
            return `${value.storeID}-${value.rxNumber}-${value.rfNumber}`;
        };
        private dateFormatter = (value: string) => {
            // moment reads the exporter's empty string as an invalid date and renders it as the
            // literal "Invalid date"; a line with no dispense date exports an empty cell instead.
            if (!value) {
                return "";
            }
            return moment.utc(value).format('L');
        };
        private deaClassificationFormatter = (value: number) => {
            switch (value) {
                case 1: return "I";
                case 2: return "II";
                case 3: return "III";
                case 4: return "IV";
                case 5: return "V";

                default: return "N/A";
            }
        };
        private quantityFormatter = (value: any) => {
            if (value == null) return "";
            return Number(value).toString();
        };
        private reportFields = [
            { label: "Dispensed On", key: "created", formatter: this.dateFormatter, sortable: true, },
            { label: "Prescription", key: "prescription", formatter: this.prescriptionIdFormatter, },
            { label: "Prescriber", key: "prescriber", formatter: this.nameFormatter, sortable: true, },
            { label: "Patient", key: "patient", formatter: this.nameFormatter, sortable: true, },
            { label: "Store", key: "store.name", sortable: true, },
            { label: "Drug Name", key: "productNameShort", sortable: true, },
            { label: "Quantity Dispensed", key: "prescription.dispensed", formatter: this.quantityFormatter, sortable: true, },
            { label: "NDC", key: "ndc", formatter: this.ndcFormatter, sortable: true, },
            { label: "DEA Schedule", key: "deaClassificationID", formatter: this.deaClassificationFormatter, sortable: true, },
            { label: "Program", key: "program.name", sortable: true, },
        ];
        get pdfFields():Array<any> {
            const res = this.reportFields.map(f => {return {label: f.label, key: f.key, formatter: f.formatter}});
            return res;
        }

        //paging (pageNumber is 1-indexed to match BottomPagerBar/b-pagination; the API expects a 0-indexed page)
        private pageSize: number = 50;
        private pageNumber: number = 1;
        private totalCount: number = 0;

        @Watch('fromDate')
        @Watch('toDate')
        @Watch('deaSelection')
        filterFromFirstPage(){
            this.pageNumber = 1;
            this.getReport();
        }

        onPageChange(page: number){
            this.pageNumber = page;
            this.getReport();
        }

        onPageSizeChange(size: number){
            this.pageSize = size;
            this.pageNumber = 1;
            this.getReport();
        }

        handleSort(ctx: { sortBy: string; sortDesc: boolean }){
            this.sortBy = ctx.sortBy;
            this.sortDesc = ctx.sortDesc;
            this.pageNumber = 1;
            this.getReport();
        }

        //data fetch
        getReport() {
            this.isLoading = true;
            this.getReportPromise(this.pageNumber - 1, this.pageSize)
            .then(response => {
                const results: any[] = response?.data ?? [];
                this.reportItems = results;
                this.totalCount = results[0]?.totalCount ?? 0;
            })
            .catch(err => {
                let errorMsg = "Error loading dispense report";
                this.$notification(NotificationOptions.notificationOptionsPreset(errorMsg, NotificationOptions.NotificationTypes.danger));
                console.error(errorMsg, {err, response: err?.response })
            })
            .finally(() => {
                this.isLoading = false;
            });
        }

        getReportPromise(pageNumber:number|null = null, pageSize:number|null = null):Promise<any> {
            //Pass from a array of bool values to a comma separated string list of selected DEA Classifications
            const deaClassifications = this.deaSelection.map((value, ifx) => {return {value, deaClass : ifx + 1}})
                .filter(i => i.value).map(i => i.deaClass).join(',');

            const config = {
                params: {
                    fromDate: moment(this.fromDate).startOf('day').utc().toDate(),
                    toDate: moment(this.toDate).startOf('day').utc().toDate(),
                    programID: this.program?.id,
                    storeID: this.store?.id,
                    deaClassifications,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
            };
            let pagingArguments = pageSize === null ? "" : `pageSize=${pageSize}`
            pagingArguments += pageNumber === null ? "" : `&pageNumber=${pageNumber}`
            const reportPath = `/prescription/Dispensed?${pagingArguments}`;
            return new Promise<any>((resolve, reject) => {
                this.$http.get(reportPath, config)
                    .then(res => {
                        resolve(res);
                    })
                    .catch(err => {
                        reject(err);
                    });
            })
        }

        get isDisabled():boolean {
            return this.isLoading;
        }

        // button methods
        private pdfData: any[] = [];
        private loadingPDF = false;
        savePDF() {
            this.loadingPDF = true;
            this.getReportPromise()
            .then(res => {
                this.pdfData = res.data;
                this.$nextTick(() => {
                    this.ExportPDF("Dispensed Report", "#pdf-table")
                });
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
                console.error(err);
            })
            .finally(() => {
                this.loadingPDF = false;
            });
        }

        private loadingCSV = false;
        saveCSV() {
            this.loadingCSV = true;
            this.getReportPromise()
            .then(res => {
                this.ExportCSV("Dispensed Report", res.data, this.reportFields);
            })
            .catch(err => {
                let errorMsg = "Error loading dispense report to export to CSV";
                this.$notification(NotificationOptions.notificationOptionsPreset(errorMsg, NotificationOptions.NotificationTypes.danger));
                console.error(errorMsg, {err, response: err?.response })
            })
            .finally(() => {
                this.loadingCSV = false;
            });
        }

        allClicked() {
            this.fromDate = moment("01/01/2000", "MM/dd/yyyy").toDate();
            this.toDate = moment().add(1, "days").toDate();
        }
    }
</script>

<style scoped>
</style>
